import { useState } from 'react'
import type { ReactNode } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useConfirm } from '../../lib/confirm'
import { titleCase, formatDateTime } from '../../lib/format'
import type { Organization } from '../../types'
import { Alert, Badge, Button, Card, CardBody, CardHeader, Skeleton, Textarea } from '../../components/ui'
import { CheckIcon } from '../../components/icons'

/* Faithful port of anansi/app/templates/admin/compliance_detail.html + its
 * view admin/routes.py:compliance_detail() and the two decision routes
 * compliance_approve()/compliance_request_changes(). */

interface ChecklistDoc { url?: string }
interface SafetyOfficer { name?: string; qualification?: string; qualification_document?: ChecklistDoc }
interface Checklist {
  org_id: string
  safety_ack: { acknowledged?: boolean }
  tc_ack: { accepted?: boolean }
  license_choice: string
  license_document: ChecklistDoc
  safety_officer: SafetyOfficer
  submitted_at: string
  submitted_by: string
  reviewed_at: string
  reviewed_by: string
  review_notes: string
  approved_with_exceptions?: boolean
  approval_exception_items?: string[]
}
interface Progress {
  items: {
    staff: boolean; safety_ack: boolean; tc_ack: boolean
    license: boolean; safety_officer: boolean; equipment: boolean
  }
  staff_count: number
  staff_required: number
  is_complete: boolean
}
interface UserRef { full_name?: string; email?: string }
interface ComplianceDetailResponse {
  ok: boolean
  org: Organization
  checklist: Checklist
  progress: Progress
  submitted_by: UserRef | null
  reviewed_by: UserRef | null
}

type Tone = 'success' | 'danger' | 'warning' | 'neutral'
function statusTone(status: string): Tone {
  if (status === 'approved') return 'success'
  if (status === 'changes_requested') return 'danger'
  if (status === 'incomplete_governance') return 'warning'
  return 'warning'
}

function ItemRow({ label, done }: { label: string; done: boolean }) {
  return (
    <div className="row" style={{ gap: 'var(--sp-2)' }}>
      {done
        ? <span style={{ color: 'var(--success)', flexShrink: 0 }}><CheckIcon size={16} /></span>
        : <span style={{
            width: 14, height: 14, borderRadius: '50%', border: '1.5px solid var(--border)', flexShrink: 0,
          }} />}
      <span style={{ color: done ? 'var(--text)' : 'var(--text-muted)', fontWeight: done ? 550 : 400 }}>{label}</span>
    </div>
  )
}

function personLabel(ref: UserRef | null, uid: string): string {
  if (ref?.full_name) return ref.full_name
  if (ref?.email) return ref.email
  return uid.slice(0, 12)
}

export default function AdminComplianceDetail() {
  const { orgId } = useParams<{ orgId: string }>()
  const qc = useQueryClient()
  const confirm = useConfirm()
  const [notes, setNotes] = useState('')

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'compliance', 'detail', orgId],
    queryFn: () => api.get<ComplianceDetailResponse>(`/admin/compliance/${orgId}`),
    enabled: Boolean(orgId),
  })

  const approve = useMutation({
    mutationFn: (override: boolean) =>
      api.post<{ ok: boolean }>(`/admin/compliance/${orgId}/approve`, { notes, override }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'compliance'] }),
  })
  const requestChanges = useMutation({
    mutationFn: () => api.post<{ ok: boolean }>(`/admin/compliance/${orgId}/request-changes`, { notes }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'compliance'] }),
  })

  if (!orgId) return <Navigate to="/compliance" replace />
  if (isError) {
    return (
      <Alert tone="danger">
        {(error as Error).message}
        <div style={{ marginTop: 'var(--sp-3)' }}><Link to="/compliance">Back to queue</Link></div>
      </Alert>
    )
  }
  if (isLoading || !data) return <div className="stack"><Skeleton h={30} w="40%" /><Skeleton h={160} /></div>

  const { org, checklist, progress, submitted_by, reviewed_by } = data
  const st = org.registration_status || 'pending_setup'
  const items = progress.items
  const doneCount = Object.values(items).filter(Boolean).length
  const decidable = st === 'pending_approval' || st === 'changes_requested'
  const mutationError = approve.error ?? requestChanges.error

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{org.name}</h1>
          <p className="page-head__sub">
            {titleCase(org.type)}
            {org.region_id ? ` · ${titleCase(org.region_id)}` : ''}
            {org.district_id ? `, ${titleCase(org.district_id)}` : ''}
          </p>
        </div>
        <Badge tone={statusTone(st)}>{titleCase(st)}</Badge>
      </div>

      {st === 'approved' && checklist.approved_with_exceptions && (
        <div style={{ marginBottom: 'var(--sp-6)' }}>
          <Alert tone="warning">
            <strong>Approved with an outstanding checklist.</strong> This organisation was approved to
            operate before every compliance item was complete
            {checklist.approval_exception_items?.length
              ? ` (still pending: ${checklist.approval_exception_items.map(titleCase).join(', ')})`
              : ''}
            . The items below remain pending until the organisation completes them.
            {checklist.review_notes && <> Reason given: &ldquo;{checklist.review_notes}&rdquo;</>}
          </Alert>
        </div>
      )}

      {(st === 'incomplete_governance' || checklist.submitted_by || checklist.reviewed_by) && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardBody>
            {st === 'incomplete_governance' && (
              <div style={{ marginBottom: 'var(--sp-4)' }}>
                <Alert tone="warning">
                  This cooperative&rsquo;s governance (officers / audit / management committee) doesn&rsquo;t
                  yet meet the Co-operative Societies Act minimums. It must be fixed via the Org Roles
                  screen before a compliance checklist can be meaningfully reviewed.
                  {(org as { governance_error?: string }).governance_error && (
                    <div style={{ marginTop: 4, fontStyle: 'italic' }}>
                      {(org as { governance_error?: string }).governance_error}
                    </div>
                  )}
                </Alert>
              </div>
            )}

            {(checklist.submitted_by || checklist.reviewed_by) && (
              <div className="grid grid--2">
                {checklist.submitted_by && (
                  <>
                    <Row label="Submitted by" value={personLabel(submitted_by, checklist.submitted_by)} />
                    <Row label="Submitted at" value={<span style={{ fontFamily: 'monospace', fontSize: 'var(--text-xs)' }}>{formatDateTime(checklist.submitted_at)}</span>} />
                  </>
                )}
                {checklist.reviewed_by && (
                  <>
                    <Row label="Last reviewed by" value={personLabel(reviewed_by, checklist.reviewed_by)} />
                    <Row label="Last reviewed at" value={<span style={{ fontFamily: 'monospace', fontSize: 'var(--text-xs)' }}>{formatDateTime(checklist.reviewed_at)}</span>} />
                    {checklist.review_notes && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <Row label="Previous reviewer notes" value={<em>{checklist.review_notes}</em>} />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      )}

      <Card style={{ marginBottom: 'var(--sp-6)' }}>
        <CardHeader title="Checklist" sub={`${doneCount} of 6 complete`} />
        <CardBody>
          <div className="grid grid--2" style={{ marginBottom: 'var(--sp-5)' }}>
            <ItemRow label={`Minimum staff registered (${progress.staff_count} of ${progress.staff_required})`} done={items.staff} />
            <ItemRow label="Safety protocols acknowledged" done={items.safety_ack} />
            <ItemRow label="Community terms accepted" done={items.tc_ack} />
            <ItemRow label="Mining licence / partnership choice" done={items.license} />
            <ItemRow label="Safety officer on file" done={items.safety_officer} />
            <ItemRow label="Equipment registered" done={items.equipment} />
          </div>

          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 'var(--sp-4)' }} className="stack">
            {checklist.license_choice && (
              <Row
                label="Licence"
                value={
                  checklist.license_choice === 'partner' ? (
                    'Operating as a partner under SSM Zambia’s licence.'
                  ) : (
                    <>
                      Holds their own mining licence.{' '}
                      {checklist.license_document?.url ? (
                        <a href={checklist.license_document.url} target="_blank" rel="noopener noreferrer">View document</a>
                      ) : (
                        <span style={{ color: 'var(--danger)', fontWeight: 600, fontSize: 'var(--text-xs)' }}>No document uploaded</span>
                      )}
                    </>
                  )
                }
              />
            )}
            {checklist.safety_officer?.name && (
              <Row
                label="Safety officer"
                value={
                  <>
                    {checklist.safety_officer.name}
                    {checklist.safety_officer.qualification ? ` — ${checklist.safety_officer.qualification}` : ''}{' '}
                    {checklist.safety_officer.qualification_document?.url ? (
                      <a href={checklist.safety_officer.qualification_document.url} target="_blank" rel="noopener noreferrer">View document</a>
                    ) : (
                      <span style={{ color: 'var(--danger)', fontWeight: 600, fontSize: 'var(--text-xs)' }}>No document uploaded</span>
                    )}
                  </>
                }
              />
            )}
          </div>
        </CardBody>
      </Card>

      <Card>
        {decidable ? (
          <>
            <CardHeader title="Decision" />
            <CardBody>
              {mutationError && (
                <div style={{ marginBottom: 'var(--sp-3)' }}>
                  <Alert tone="danger">{mutationError instanceof ApiError ? mutationError.message : 'That action failed.'}</Alert>
                </div>
              )}
              {!progress.is_complete && (
                <div style={{ marginBottom: 'var(--sp-4)' }}>
                  <Alert tone="warning">
                    The checklist isn&rsquo;t fully complete yet. You can still approve now with a reason —
                    the outstanding item(s) stay pending on the organisation&rsquo;s own record until they
                    complete them — or send it back with notes instead.
                  </Alert>
                </div>
              )}
              <Textarea
                label={progress.is_complete ? 'Notes (optional)' : 'Reason (required to approve early)'}
                rows={2}
                placeholder={progress.is_complete ? 'What did you verify?' : 'Why approve before the checklist is complete?'}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
              <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--sp-2)', marginTop: 'var(--sp-3)' }}>
                <Button
                  disabled={!progress.is_complete && !notes.trim()}
                  loading={approve.isPending}
                  onClick={async () => {
                    const message = progress.is_complete
                      ? 'Approve this organisation? It will immediately be able to register sites, log production and dispatch loads.'
                      : 'Approve with an incomplete checklist? The organisation can operate immediately; the outstanding item(s) stay pending until it completes them.'
                    if (await confirm({ title: 'Approve organisation?', message })) {
                      approve.mutate(!progress.is_complete)
                    }
                  }}
                >
                  {progress.is_complete ? 'Approve' : 'Approve with reason'}
                </Button>
                <Button
                  variant="danger"
                  loading={requestChanges.isPending}
                  onClick={async () => {
                    if (!notes.trim()) return
                    if (await confirm({ title: 'Send back for changes?', destructive: true })) {
                      requestChanges.mutate()
                    }
                  }}
                  disabled={!notes.trim()}
                >
                  Request changes
                </Button>
                <Link to="/compliance" className="btn btn--ghost btn--sm">Back to queue</Link>
              </div>
              <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)', marginTop: 'var(--sp-3)' }}>
                Requesting changes requires notes explaining what to fix — the organisation sees them.
              </p>
            </CardBody>
          </>
        ) : (
          <CardBody>
            <Link to="/compliance">← Back to queue</Link>
          </CardBody>
        )}
      </Card>
    </>
  )
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontWeight: 550 }}>{value}</div>
    </div>
  )
}
